// Customer pricing summary sheet (route /customers/:id/pricing-sheet — rendered
// OUTSIDE AppShell/MuiAppShell so the page itself is the printable document).
//
// MUI + brainTokens restyle of PricingSheetPage. Print/Save-as-PDF and data
// loading behavior match the original.
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CssBaseline,
  FormControlLabel,
  GlobalStyles,
  MenuItem,
  Select,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { Link, useParams } from "react-router-dom";
import {
  getFinanceCustomer,
  getFinanceCatalog,
  saveFinanceSettings,
  type CustomerPricing,
  type FinanceCustomerCosts,
  type PricingCatalog,
  type PricingCatalogItem,
  type PricingLine,
  type PricingSection,
} from "../../lib/brain-api";
import { brainTheme, brainTokens } from "../../design/brainTheme";
import { Icon } from "../../mui";

const c = brainTokens.color;

const PRINT_STYLES = {
  "@media print": {
    ".ps-root": { background: `${c.white} !important`, padding: "0 !important" },
    ".ps-no-print": { display: "none !important" },
    ".ps-paper": {
      boxShadow: "none !important",
      maxWidth: "none !important",
      padding: "0 !important",
    },
  },
  "@page": { size: "A4 landscape", margin: "12mm" },
};

const SECTION_TITLES: Record<PricingSection, string> = {
  subscription: "Aeronet subscriptions",
  service: "Service",
  infrastructure: "Infrastructure",
};

const money = (n: number): string => `$${(Math.round(n * 100) / 100).toLocaleString("en-US")}`;

function hostAlias(url?: string): string {
  try {
    return url ? new URL(url).hostname.split(".")[0] : "—";
  } catch {
    return url ?? "—";
  }
}

const lineTotal = (l: PricingLine): number => l.amount * (l.qty ?? 1);
const isMonthly = (l: PricingLine): boolean => (l.unit ?? "per month") === "per month";

function dataPlanKey(bytes: number): string {
  const gb = bytes / 1024 ** 3;
  if (gb <= 1) return "infra.dataplan.0-1";
  if (gb <= 5) return "infra.dataplan.1-5";
  if (gb <= 20) return "infra.dataplan.5-20";
  return "infra.dataplan.20+";
}

function productSlug(p: string): string | null {
  const v = p.toLowerCase();
  if (v.includes("full")) return "full-suite";
  if (v.includes("mro") || v.includes("repair")) return "mro";
  if (v.includes("ops") || v.includes("operation")) return "ops";
  if (v.includes("mt") || v.includes("track")) return "mt";
  return null;
}

function bestGuess(data: FinanceCustomerCosts, catalog: PricingCatalog): CustomerPricing {
  const byKey = new Map(catalog.items.map((i) => [i.key, i]));
  const fromCatalog = (key: string, qty?: number): PricingLine | null => {
    const it = byKey.get(key);
    return it ? { key: it.key, section: it.section, label: it.label, amount: it.amount, unit: it.unit, ...(qty && qty > 1 ? { qty } : {}) } : null;
  };
  const lines: PricingLine[] = [];

  const installs = data.installs ?? [];
  const aircraft = installs.reduce((s, i) => s + (i.aircraft ?? 0), 0);
  const tier = aircraft > 3 ? "enterprise" : "lite";
  const slugs = [...new Set((data.product ?? []).map(productSlug).filter((s): s is string => !!s))];
  for (const slug of slugs.length ? slugs : ["full-suite"]) {
    const l = fromCatalog(`product.${slug}.${tier}`);
    if (l) lines.push(l);
  }

  const backupByMonth = new Map<string, number>();
  for (const u of data.usage) {
    if (typeof u.backupBytes === "number") backupByMonth.set(u.month, (backupByMonth.get(u.month) ?? 0) + u.backupBytes);
  }
  const latestMonth = [...backupByMonth.keys()].sort().pop();
  const plan = fromCatalog(dataPlanKey(latestMonth ? backupByMonth.get(latestMonth)! : 0));
  if (plan) lines.push(plan);

  for (const key of ["infra.backups.daily", "infra.backups.archive"]) {
    const l = fromCatalog(key);
    if (l) lines.push(l);
  }

  const extra = installs.slice(1);
  const kind = (alias?: string) => (/test/i.test(alias ?? "") ? "test" : /train/i.test(alias ?? "") ? "training" : "live");
  const counts = new Map<string, number>();
  for (const s of extra) counts.set(kind(s.alias), (counts.get(kind(s.alias)) ?? 0) + 1);
  for (const [k, qty] of counts) {
    const l = fromCatalog(`infra.install.${k}`, qty);
    if (l) lines.push(l);
  }

  return { lines };
}

const tableSx = {
  width: "100%",
  borderCollapse: "collapse",
  "& td, & th": {
    py: "9px",
    pr: 1,
    pl: 0,
    fontSize: 13,
    borderBottom: `1px solid ${c.border}`,
    textAlign: "left",
    verticalAlign: "middle",
  },
  "& th": {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: c.muted2,
  },
};

const amountSx = {
  textAlign: "right !important",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
};

const unitSx = { color: c.muted2, fontSize: 11 };

const sectionLabelSx = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.15em",
  textTransform: "uppercase",
  color: c.ink,
  borderBottom: `2px solid ${c.ink}`,
  pb: "6px",
  m: 0,
  mb: "4px",
};

const editFieldSx = {
  "& .MuiInputBase-root": { fontSize: 13, bgcolor: c.panelBg3 },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: c.border },
};

export function PricingSheetPageMui() {
  const { id = "" } = useParams();
  const [data, setData] = useState<FinanceCustomerCosts | null>(null);
  const [catalog, setCatalog] = useState<PricingCatalog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<CustomerPricing | null>(null);
  const [seeded, setSeeded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([getFinanceCustomer(id), getFinanceCatalog()])
      .then(([d, cat]) => {
        if (!alive) return;
        setData(d);
        setCatalog(cat.catalog);
        if (d.finance.pricing?.lines?.length) {
          setDraft(d.finance.pricing);
        } else {
          setDraft(bestGuess(d, cat.catalog));
          setSeeded(true);
          setEditing(true);
        }
      })
      .catch((e) => {
        if (alive) setError(e instanceof Error ? e.message : "Failed to load.");
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const totals = useMemo(() => {
    if (!draft) return null;
    const sum = (section: PricingSection) =>
      draft.lines.filter((l) => l.section === section && isMonthly(l)).reduce((s, l) => s + lineTotal(l), 0);
    const subs = sum("subscription");
    const infra = sum("infrastructure");
    const serviceMonthly = sum("service");
    const discount = draft.discountMonthly ?? 0;
    const oneOffs = draft.lines.filter((l) => !isMonthly(l));
    return { subs, infra, serviceMonthly, discount, monthly: subs + infra + serviceMonthly - discount, oneOffs };
  }, [draft]);

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setSaveErr(null);
    try {
      const saved = await saveFinanceSettings(id, { pricing: draft });
      if (saved.pricing) setDraft(saved.pricing);
      setSeeded(false);
      setEditing(false);
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const patchLine = (i: number, patch: Partial<PricingLine>) =>
    setDraft((d) => d && { ...d, lines: d.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const removeLine = (i: number) => setDraft((d) => d && { ...d, lines: d.lines.filter((_, j) => j !== i) });
  const addLine = (l: PricingLine) => setDraft((d) => d && { ...d, lines: [...d.lines, l] });

  const rootSx = {
    minHeight: "100vh",
    bgcolor: c.ink3,
    py: "24px",
    px: "12px",
    pb: "64px",
    fontFamily: brainTokens.font.sans,
  };

  const toolbarSx = {
    maxWidth: 1120,
    mx: "auto",
    mb: 2,
    display: "flex",
    alignItems: "center",
    gap: 1,
    color: c.white,
    "& a": {
      color: c.faint2,
      textDecoration: "none",
      fontSize: 13,
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      "&:hover": { color: c.white },
    },
  };

  const paperSx = {
    maxWidth: 1120,
    mx: "auto",
    bgcolor: c.white,
    color: c.ink,
    p: "40px 48px 48px",
    boxShadow: "0 6px 32px rgba(0,0,0,.35)",
  };

  const page = (body: React.ReactNode) => (
    <ThemeProvider theme={brainTheme}>
      <CssBaseline />
      <GlobalStyles styles={PRINT_STYLES} />
      <Box className="ps-root" sx={rootSx}>
        {body}
      </Box>
    </ThemeProvider>
  );

  if (error) {
    return page(
      <Box className="ps-no-print" sx={toolbarSx}>
        <Link to={`/customers/${encodeURIComponent(id)}?tab=finance`}>
          <Icon name="arrow-left" size={14} /> Back
        </Link>
        <Box component="span" sx={{ color: c.dangerText3, fontSize: 13 }}>
          {error}
        </Box>
      </Box>,
    );
  }

  if (!data || !catalog || !draft || !totals) {
    return page(
      <Box className="ps-no-print" sx={toolbarSx}>
        Loading pricing sheet…
      </Box>,
    );
  }

  const currency = data.finance.currency ?? "NZD";
  const name = (data.name ?? data.customerId).toUpperCase();
  const hasChanges = draft.lines.some((l) => l.changed) || !!draft.note;

  const sectionRows = (section: PricingSection) => draft.lines.map((l, i) => ({ l, i })).filter(({ l }) => l.section === section);

  const lineRow = ({ l, i }: { l: PricingLine; i: number }) => (
    <Box component="tr" key={i}>
      <Box component="td" sx={{ ...(l.changed ? { color: c.dangerText } : {}) }}>
        {editing ? (
          <TextField
            size="small"
            value={l.label}
            onChange={(e) => patchLine(i, { label: e.target.value })}
            sx={{ ...editFieldSx, width: "100%", minWidth: 160 }}
          />
        ) : (
          <>
            {l.label}
            {(l.qty ?? 1) > 1 ? ` × ${l.qty}` : ""}
          </>
        )}
      </Box>
      {editing && (
        <Box component="td">
          <TextField
            size="small"
            type="number"
            inputProps={{ min: 1 }}
            value={l.qty ?? 1}
            onChange={(e) => patchLine(i, { qty: Math.max(1, Number(e.target.value) || 1) })}
            sx={{ ...editFieldSx, width: 52 }}
          />
        </Box>
      )}
      <Box component="td" sx={{ ...amountSx, ...(l.changed ? { color: c.dangerText } : {}) }}>
        {editing ? (
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, justifyContent: "flex-end" }}>
            <TextField
              size="small"
              type="number"
              inputProps={{ min: 0 }}
              value={l.amount}
              onChange={(e) => patchLine(i, { amount: Math.max(0, Number(e.target.value) || 0) })}
              sx={{ ...editFieldSx, width: 84, "& input": { textAlign: "right" } }}
            />
            <Select
              size="small"
              value={l.unit ?? "per month"}
              onChange={(e) => patchLine(i, { unit: e.target.value })}
              sx={{ ...editFieldSx, fontSize: 13, minWidth: 100 }}
            >
              <MenuItem value="per month">per month</MenuItem>
              <MenuItem value="per day">per day</MenuItem>
              <MenuItem value="one-off">one-off</MenuItem>
            </Select>
            <FormControlLabel
              sx={{ m: 0, "& .MuiTypography-root": { fontSize: 11, whiteSpace: "nowrap" } }}
              control={
                <Checkbox
                  size="small"
                  checked={!!l.changed}
                  onChange={(e) => patchLine(i, { changed: e.target.checked || undefined })}
                />
              }
              label="red"
            />
            <Button
              className="ps-no-print"
              size="small"
              onClick={() => removeLine(i)}
              title="Remove line"
              sx={{ minWidth: 0, color: c.dangerText, fontSize: 14, px: "6px" }}
            >
              ✕
            </Button>
          </Box>
        ) : l.amount === 0 ? (
          <Box component="span" sx={unitSx}>
            included
          </Box>
        ) : (
          <>
            {money(lineTotal(l))}{" "}
            <Box component="span" sx={unitSx}>
              {l.unit ?? "per month"}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );

  const addBar = (section: PricingSection) =>
    editing && (
      <Box className="ps-no-print" sx={{ display: "flex", gap: "6px", mt: 1, alignItems: "center" }}>
        <Select
          size="small"
          displayEmpty
          value=""
          onChange={(e) => {
            const it = catalog.items.find((item) => item.key === e.target.value);
            if (it) addLine({ key: it.key, section: it.section, label: it.label, amount: it.amount, unit: it.unit, changed: true });
            else if (e.target.value === "__custom__") addLine({ section, label: "Custom line", amount: 0, unit: "per month", changed: true });
          }}
          sx={{ ...editFieldSx, maxWidth: 300, fontSize: 13 }}
        >
          <MenuItem value="">+ Add {SECTION_TITLES[section].toLowerCase()} line…</MenuItem>
          {catalog.items.filter((item: PricingCatalogItem) => item.section === section).map((item) => (
            <MenuItem key={item.key} value={item.key}>
              {item.label} — {money(item.amount)} {item.unit}
              {item.note ? ` (${item.note})` : ""}
            </MenuItem>
          ))}
          <MenuItem value="__custom__">Custom line…</MenuItem>
        </Select>
      </Box>
    );

  return page(
    <>
      <Box className="ps-no-print" sx={toolbarSx}>
        <Link to={`/customers/${encodeURIComponent(id)}?tab=finance`}>
          <Icon name="arrow-left" size={14} /> Back to {data.name ?? id}
        </Link>
        <Box sx={{ flex: 1 }} />
        {saveErr && (
          <Box component="span" sx={{ color: c.dangerText3, fontSize: 13 }}>
            {saveErr}
          </Box>
        )}
        {editing ? (
          <>
            <Box component="span" sx={{ fontSize: 12, color: c.faint2 }}>
              Editing — values land on the customer record when saved
            </Box>
            <Button size="small" variant="outlined" onClick={() => setEditing(false)} sx={{ color: c.white, borderColor: "rgba(255,255,255,.35)" }}>
              Done
            </Button>
            <Button size="small" variant="contained" disabled={busy} onClick={() => void save()} startIcon={<Icon name="device-floppy" size={14} />}>
              Save
            </Button>
          </>
        ) : (
          <>
            <Button size="small" variant="outlined" onClick={() => setEditing(true)} startIcon={<Icon name="edit" size={14} />} sx={{ color: c.white, borderColor: "rgba(255,255,255,.35)" }}>
              Edit
            </Button>
            <Button size="small" variant="contained" onClick={() => window.print()} startIcon={<Icon name="printer" size={14} />}>
              Print / PDF
            </Button>
          </>
        )}
      </Box>

      {seeded && (
        <Box
          className="ps-no-print"
          sx={{
            maxWidth: 1120,
            mx: "auto",
            mb: 1.5,
            bgcolor: c.warnBg,
            border: `1px solid ${c.warnBorder}`,
            color: c.warnText3,
            fontSize: 13,
            p: "10px 14px",
            borderRadius: `${brainTokens.radius.button}px`,
          }}
        >
          Best guess assembled from live data (products, {data.installs?.length ?? 0} install
          {(data.installs?.length ?? 0) === 1 ? "" : "s"}, backup size, aircraft count) — review the lines, adjust, and save.
        </Box>
      )}

      <Box className="ps-paper" sx={paperSx}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 3 }}>
          <Box>
            <Box
              component="h1"
              sx={{
                fontSize: 26,
                fontWeight: 700,
                letterSpacing: "0.25em",
                color: c.teal,
                textTransform: "uppercase",
                m: 0,
              }}
            >
              {name} — Pricing summary.
            </Box>
            <Box component="p" sx={{ fontSize: 11, fontStyle: "italic", color: c.ink4, mt: "6px", mb: 0 }}>
              All pricing is in{" "}
              {currency === "NZD" ? "New Zealand Dollars and totals are exclusive of GST" : `${currency} and excludes any taxes that may apply`}.
            </Box>
            {hasChanges && (
              <Box component="p" sx={{ fontSize: 11, fontStyle: "italic", color: c.dangerText, mt: "6px", mb: 0 }}>
                Changes and additions indicated in red.
              </Box>
            )}
          </Box>
          <Box sx={{ fontSize: 22, fontWeight: 500, color: c.muted2, whiteSpace: "nowrap" }}>
            ∴ aero
            <Box component="b" sx={{ color: c.teal, fontWeight: 700 }}>
              net
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 5,
            mt: 4,
          }}
        >
          <Box>
            <Box component="h2" sx={sectionLabelSx}>
              {SECTION_TITLES.subscription}
            </Box>
            <Box component="table" sx={tableSx}>
              <Box component="tbody">
                {sectionRows("subscription").map(lineRow)}
                <Box component="tr" sx={{ "& td": { fontWeight: 700, borderBottom: "none", borderTop: `2px solid ${c.ink}` } }}>
                  <Box component="td" colSpan={editing ? 2 : 1}>
                    Total monthly subscriptions
                  </Box>
                  <Box component="td" sx={amountSx}>
                    {money(totals.subs)}{" "}
                    <Box component="span" sx={unitSx}>
                      per month
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
            {addBar("subscription")}
          </Box>

          <Box>
            <Box component="h2" sx={sectionLabelSx}>
              {SECTION_TITLES.service}
            </Box>
            <Box component="table" sx={tableSx}>
              <Box component="tbody">
                {sectionRows("service").length === 0 && !editing ? (
                  <Box component="tr">
                    <Box component="td">
                      <Box component="span" sx={unitSx}>
                        No service items this period.
                      </Box>
                    </Box>
                    <Box component="td" />
                  </Box>
                ) : (
                  sectionRows("service").map(lineRow)
                )}
              </Box>
            </Box>
            {addBar("service")}
          </Box>
        </Box>

        <Box sx={{ mt: "36px" }}>
          <Box component="h2" sx={sectionLabelSx}>
            {SECTION_TITLES.infrastructure}
          </Box>
          <Box component="table" sx={tableSx}>
            <Box component="tbody">
              {sectionRows("infrastructure").map(lineRow)}
              <Box component="tr" sx={{ "& td": { fontWeight: 700, borderBottom: "none", borderTop: `2px solid ${c.ink}` } }}>
                <Box component="td" colSpan={editing ? 2 : 1}>
                  Total monthly infrastructure
                </Box>
                <Box component="td" sx={amountSx}>
                  {money(totals.infra)}{" "}
                  <Box component="span" sx={unitSx}>
                    per month
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          {addBar("infrastructure")}
        </Box>

        {(data.installs?.length ?? 0) > 0 && (
          <Box sx={{ mt: "36px" }}>
            <Box component="h2" sx={sectionLabelSx}>
              Current installs
            </Box>
            <Box component="table" sx={tableSx}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th">Install name</Box>
                  <Box component="th">URL</Box>
                  <Box component="th">Status</Box>
                  <Box component="th">Version</Box>
                  <Box component="th" sx={amountSx}>
                    Aircraft · users
                  </Box>
                </Box>
              </Box>
              <Box component="tbody">
                {data.installs!.map((s, i) => (
                  <Box component="tr" key={i}>
                    <Box component="td">{s.alias ?? hostAlias(s.appUrl)}</Box>
                    <Box component="td">{s.appUrl ?? "—"}</Box>
                    <Box component="td">Live</Box>
                    <Box component="td">{s.release ?? "—"}</Box>
                    <Box component="td" sx={amountSx}>
                      <Box component="span" sx={unitSx}>
                        {s.aircraft ?? "—"} · {s.activeUsers ?? "—"}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={{ mt: "36px", display: "flex", justifyContent: "flex-end" }}>
          <Box
            component="table"
            sx={{
              borderCollapse: "collapse",
              minWidth: 380,
              "& td": {
                py: 1,
                pl: 3,
                pr: 0,
                fontSize: 13,
                borderBottom: `1px solid ${c.border}`,
              },
              "& td:last-of-type": {
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
                fontWeight: 500,
              },
            }}
          >
            <Box component="tbody">
              <Box component="tr">
                <Box component="td">Subscriptions total:</Box>
                <Box component="td">{money(totals.subs)} per month</Box>
              </Box>
              <Box component="tr">
                <Box component="td">Infrastructure total:</Box>
                <Box component="td" sx={totals.infra > 0 ? undefined : unitSx}>
                  {money(totals.infra)} per month
                </Box>
              </Box>
              {totals.serviceMonthly > 0 && (
                <Box component="tr">
                  <Box component="td">Services (monthly):</Box>
                  <Box component="td">{money(totals.serviceMonthly)} per month</Box>
                </Box>
              )}
              <Box component="tr">
                <Box component="td">Current discount:</Box>
                <Box component="td">
                  {editing ? (
                    <TextField
                      size="small"
                      type="number"
                      inputProps={{ min: 0 }}
                      value={draft.discountMonthly ?? 0}
                      onChange={(e) =>
                        setDraft((d) => d && { ...d, discountMonthly: Math.max(0, Number(e.target.value) || 0) || undefined })
                      }
                      sx={{ ...editFieldSx, width: 84, "& input": { textAlign: "right" } }}
                    />
                  ) : (
                    <>{draft.discountMonthly ? `−${money(draft.discountMonthly)}` : "*$0.00"} per month</>
                  )}
                </Box>
              </Box>
              <Box
                component="tr"
                sx={{
                  "& td": {
                    fontSize: 15,
                    fontWeight: 700,
                    color: c.dangerText,
                    borderBottom: "none",
                    borderTop: `2px solid ${c.ink}`,
                  },
                }}
              >
                <Box component="td">Total monthly cost:</Box>
                <Box component="td">
                  {currency} {money(totals.monthly)} per month
                </Box>
              </Box>
              {totals.oneOffs.map((l, i) => (
                <Box
                  component="tr"
                  key={i}
                  sx={{
                    "& td": {
                      fontSize: 15,
                      fontWeight: 700,
                      color: c.dangerText,
                      borderBottom: "none",
                      borderTop: `2px solid ${c.ink}`,
                    },
                  }}
                >
                  <Box component="td">Total for {l.label}:</Box>
                  <Box component="td">
                    {currency} {money(lineTotal(l))}{" "}
                    {l.unit === "per day" ? `(${l.qty ?? 1} day${(l.qty ?? 1) === 1 ? "" : "s"})` : "one-off cost"}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {(editing || draft.note) && (
          <Box component="p" sx={{ fontSize: 11, fontWeight: 600, color: c.dangerText, mt: 3, mb: 0 }}>
            {editing ? (
              <TextField
                size="small"
                fullWidth
                placeholder="Sheet footnote (shown in red), e.g. prepayment discount expiry…"
                value={draft.note ?? ""}
                onChange={(e) => setDraft((d) => d && { ...d, note: e.target.value || undefined })}
                sx={editFieldSx}
              />
            ) : (
              <>Please note: {draft.note}</>
            )}
          </Box>
        )}

        <Box component="p" sx={{ fontSize: 10, color: c.faint, mt: "28px", mb: 0 }}>
          {draft.updatedAt ? `Pricing last reviewed ${draft.updatedAt}${draft.updatedBy ? ` by ${draft.updatedBy}` : ""} · ` : ""}
          Rate card: Aeronet Standard Pricing {catalog.year} ({catalog.currency})
        </Box>
      </Box>
    </>,
  );
}
